import axios from 'axios';
import { getApiToken, authConfigured } from './auth';
import { storedViewAsUserId, storedViewAsContractorId } from './viewAs';
import { isBlockedAnswer, markBlocked } from './blocked';
import { currentMe, fetchMe } from './me';
import { rememberReturnPath } from './returnPath';

/**
 * Attach the staff token to every call this app makes to its own API.
 *
 * Two paths need covering, because the app grew both:
 *   - axios, which most pages import directly
 *   - window.fetch, which nine components use instead
 *
 * Patching fetch rather than rewriting those nine is deliberate: a component
 * added later that reaches for fetch is covered without anyone remembering,
 * and there is no second way to get this wrong.
 *
 * Neither path ever touches a request to another host.
 */
let installed = false;

/**
 * The API marks a response as degraded when it answered with less than it
 * should - an empty list because a query could not run. Subscribers are told
 * once per response so it can be surfaced rather than mistaken for real data.
 */
const degradedListeners = new Set();

export function onDegraded(fn) {
  degradedListeners.add(fn);
  return () => degradedListeners.delete(fn);
}

/**
 * A change refused because the browser would not say where it was made.
 *
 * ANNOUNCED GLOBALLY, for the reason the degraded header above is: it can
 * happen on seven different endpoints, from a dozen different screens, and a
 * panel wired into each of them is twelve chances to forget one. The first
 * version of this work wired an explanation into two places and left the
 * other five showing somebody a raw error - which, for a rule that refuses
 * writes, is the difference between a thirty-second fix and a phone call.
 *
 * Listeners get the whole error, not a message, because only the response
 * body knows which advice applies - a denied permission and an insecure page
 * need completely different sentences.
 */
const refusedListeners = new Set();

export function onLocationRefused(fn) {
  refusedListeners.add(fn);
  return () => refusedListeners.delete(fn);
}

const announceRefused = (error) => {
  if (error?.response?.status !== 428) return;
  // The gate names itself. A 428 from anything else is not this, and guessing
  // would put location advice in front of somebody with a different problem.
  if (error.response.data?.setting !== 'require_location') return;
  refusedListeners.forEach((fn) => {
    try { fn(error); } catch { /* a listener must not break a request */ }
  });
};

/**
 * WHAT THE HEADER SAYS, since 29 October: a code, not the database's words.
 * lib/degrade.js on the server sends `<label>/<reason>` - say
 * contractor-portal/missing-table - because the header is readable by any
 * page and used to carry the error's own message, table names and all. This
 * turns the code back into the sentence the banner shows. Anything that is
 * not in that shape (an older server, a test's own text) is shown as it is.
 */
const DEGRADED_REASONS = {
  'missing-table': 'a table it needs does not exist yet - a migration has not been applied.',
  'missing-column': 'a column it needs does not exist yet - a migration has not been applied.',
  'missing-function': 'a database function it needs does not exist yet - a migration has not been applied.',
  'query-failed': 'the query could not be run. The server log has the detail.',
  'not-recorded': 'the change was made, but one thing about it was not recorded. The server log has the detail.'
};
const DEGRADED_CODE = /^([a-z0-9]+(?:-[a-z0-9]+)*)[/]([a-z-]+)$/;

export function describeDegraded(header) {
  const h = String(header || '').trim();
  const m = DEGRADED_CODE.exec(h);
  if (!m) return h;
  const label = m[1].replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
  const why = DEGRADED_REASONS[m[2]] || `it could not be loaded (${m[2]}).`;
  return `${label}: ${why}`;
}

const announceDegraded = (header) => {
  if (!header) return;
  const reason = describeDegraded(header);
  degradedListeners.forEach((fn) => {
    try { fn(reason); } catch { /* a listener must not break a request */ }
  });
};

const API_BASE = process.env.REACT_APP_API_URL || '';

/** Is this one of ours? Relative urls are, absolute ones only if they match. */
const isOurApi = (url) => {
  const u = String(url || '');
  if (u.startsWith('/')) return true;
  if (!/^https?:\/\//i.test(u)) return true;
  return API_BASE ? u.startsWith(API_BASE) : false;
};

/**
 * A header of `Bearer null` is what several callers send when they read a
 * token out of localStorage that is not there any more. It is not a credential
 * and must not stop a real one being attached.
 */
const hasRealBearer = (value) => {
  if (!value) return false;
  const t = String(value).replace(/^Bearer\s+/i, '').trim();
  return t !== '' && t !== 'null' && t !== 'undefined';
};

/**
 * A 401 FROM OUR OWN API, handled once for every screen.
 *
 * There was no global handling at all. lib/auth's getApiToken() answers null
 * when a token cannot be had, the request goes out anonymous, and the page
 * that made it gets a bare 401 to do what it likes with - which on My
 * tickets was navigate('/'), and / for a member of staff is /my-tickets,
 * which mounted My tickets again, which fetched, got 401, and so on without
 * end or a word of explanation.
 *
 * So: a 401 on a request that went out WITHOUT the contractor's own bearer,
 * while the app believed a member of staff was signed in, means the session
 * it believed in has gone. /auth/me is asked again, forced; if the server
 * now says nobody is signed in, every route's own guard sends them to the
 * front door - and where they were is remembered first (lib/returnPath), so
 * signing in again lands them back on the page the 401 interrupted.
 *
 * Not for a request carrying a portal token: a contractor's expiry is
 * handled by the portal pages, which know to send them to the code door.
 * Not for /auth/me itself, which decides this rather than being subject to
 * it. And never more than one re-check at a time.
 */
let recheckingMe = false;

const isAuthProbe = (url) => /\/auth\/me(\?|$)/.test(String(url || '').split('#')[0]);

/**
 * `callerSigned`: the request carried a bearer the CALLER put there - a
 * contractor page sending the portal token - rather than one this module
 * attached (or none at all). Those are not ours to decide.
 */
const unauthorised = (url, { callerSigned = false } = {}) => {
  if (!isOurApi(url) || isAuthProbe(url) || callerSigned) return;
  const me = currentMe();
  if (!me?.signedIn || recheckingMe) return;
  recheckingMe = true;
  // Remembered BEFORE the re-check answers: the redirect is the router's
  // doing the moment `me` changes, and there is no later moment to do it.
  try { rememberReturnPath(window.location.pathname + window.location.search); } catch { /* fine */ }
  fetchMe({ force: true }).catch(() => {}).finally(() => { recheckingMe = false; });
};

/**
 * Reporting a degraded answer has nothing to do with being signed in, and used
 * to sit behind the same `authConfigured` guard as attaching the token. On a
 * deployment with no Azure configuration - a fresh environment, a preview, a
 * misconfigured variable - the interceptor was never installed at all, so a
 * query that could not run came back as an empty list and said nothing. That
 * is precisely the failure the header exists to prevent, and it was switched
 * off exactly where it was most likely to be needed.
 */
let degradedInstalled = false;

export function installDegradedReporting() {
  if (degradedInstalled) return;
  degradedInstalled = true;

  // --- window.fetch ---
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!isOurApi(url)) return nativeFetch(input, init);

    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));
    const callerSigned = hasRealBearer(headers.get('Authorization'));
    if (!callerSigned) {
      try {
        const as = storedViewAsUserId();
        if (as) headers.set('X-View-As-User', as);
        const asContractor = storedViewAsContractorId();
        if (asContractor) headers.set('X-View-As-Contractor', asContractor);
        const token = await getApiToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
      } catch {
        // as above
      }
    }
    const response = await nativeFetch(input, { ...init, headers });
    announceDegraded(response.headers.get('X-Data-Degraded'));
    if (response.status === 401) unauthorised(url, { callerSigned });
    // Switched off by an administrator (lib/blocked): read from a copy, so
    // the caller still gets the body it asked for.
    if (response.status === 403) {
      response.clone().json()
        .then((b) => { if (isBlockedAnswer(403, b)) markBlocked(); })
        .catch(() => {});
    }
    return response;
  };

  /*
   * SWITCHED OFF BY AN ADMINISTRATOR (lib/blocked). Watched here, for every
   * request from every screen, for the reason the two above are: a check
   * wired into each page is a page that forgets it. /auth/me says so with a
   * 200, anything else with a 403; either swaps the app for one page.
   */
  axios.interceptors.response.use(
    (response) => {
      announceDegraded(response?.headers?.['x-data-degraded']);
      if (isOurApi(response?.config?.url) && isBlockedAnswer(response.status, response.data)) {
        markBlocked();
      }
      return response;
    },
    (error) => {
      announceDegraded(error?.response?.headers?.['x-data-degraded']);
      announceRefused(error);
      if (error?.response && isOurApi(error.config?.url)
          && isBlockedAnswer(error.response.status, error.response.data)) {
        markBlocked();
      }
      if (error?.response?.status === 401) {
        const h = error.config?.headers || {};
        const bearer = h.Authorization || h.authorization;
        // Signed by the staff interceptor below, or not signed at all: a
        // staff session the app believed in. Signed by the caller: a
        // contractor's own token, theirs to handle.
        unauthorised(error.config?.url, {
          callerSigned: hasRealBearer(bearer) && !error.config?.jarvisStaffSigned
        });
      }
      // Still rejected: the screen that made the request has to know it
      // failed. This only adds an explanation beside it.
      return Promise.reject(error);
    }
  );
}

/**
 * WHERE A CHANGE WAS MADE, attached to the changes that record one.
 *
 * Gordon: "I also want that the users/admins/contractors gps location are
 * logged with the tickets whenever there is a recorded change."
 *
 * Here rather than in each screen, for the reason the token is here: a page
 * added later is covered without anybody remembering, and there is no second
 * way to get it wrong. The list below mirrors lib/where.js RECORDED on the
 * server - those two agreeing is the whole contract, and e2e/location.mjs
 * compares them rather than trusting this comment.
 *
 * INSTALLED UNCONDITIONALLY, unlike the token interceptor below it, and in a
 * separate interceptor on purpose. That one returns early the moment a request
 * already carries a real bearer - which is every contractor request, since the
 * portal attaches its own token - so location logic placed inside it would
 * have covered staff and silently skipped every contractor. Gordon asked for
 * users, admins AND contractors.
 *
 * ONLY ON THE CHANGES THAT NEED IT. A location requirement on reads or on the
 * long polls would mean a GPS fix every few seconds all day: a tracking
 * device rather than an audit trail, and a flat phone by lunchtime.
 */
const RECORDS_LOCATION = [
  { method: 'POST', test: (p) => /\/tickets\/[^/]+\/comments\/?$/.test(p) },
  { method: 'PATCH', test: (p) => /\/tickets\/[^/]+\/status\/?$/.test(p) },
  { method: 'PUT', test: (p) => /\/tickets\/[^/]+\/?$/.test(p) },
  { method: 'POST', test: (p) => /\/tickets\/[^/]+\/handbacks\/?$/.test(p) },
  { method: 'POST', test: (p) => /\/tickets\/[^/]+\/time-entries\/?$/.test(p) },
  { method: 'POST', test: (p) => /\/tickets\/[^/]+\/approvals?\/[^/]+\/decide\/?$/.test(p) },
  { method: 'POST', test: (p) => /\/reopen-requests\/[^/]+\/decide\/?$/.test(p) }
];

export function recordsLocation(method, url) {
  const m = String(method || 'GET').toUpperCase();
  // The path only: a query string must not change whether a change is
  // recorded, and ?since=... is on several of these.
  const path = String(url || '').split('?')[0];
  return RECORDS_LOCATION.some((r) => r.method === m && r.test(path));
}

let locationInstalled = false;

/*
 * WHETHER TO ASK AT ALL - the firm's switch (Settings, "Ask for people's
 * location"; lib/settings.js askLocation). Read before asking the device,
 * so that with it off no browser shows the permission prompt and no fix is
 * taken. Kept for a minute, so a burst of comments costs one small read; a
 * change made in Settings reaches every open tab within that minute. When
 * the read fails it asks, as Jarvis did before the switch existed - and the
 * server ignores any fix it is sent while the switch is off, so a stale
 * answer here can never put a location on a record.
 */
const POLICY_MS = 60 * 1000;
let policy = null;   // { at, ask }

const askingForLocation = async (base) => {
  if (policy && Date.now() - policy.at < POLICY_MS) return policy.ask;
  try {
    const r = await axios.get(`${base}/location-policy`);
    policy = { at: Date.now(), ask: r.data?.ask !== false };
  } catch {
    policy = { at: Date.now(), ask: true };
  }
  return policy.ask;
};

/** For Settings, so flipping the switch takes effect in this tab at once. */
export function forgetLocationPolicy() { policy = null; }

export function installLocationInterceptor() {
  if (locationInstalled) return;
  locationInstalled = true;

  axios.interceptors.request.use(async (config) => {
    if (!isOurApi(config.url)) return config;
    if (!recordsLocation(config.method, config.url)) return config;
    try {
      if (!(await askingForLocation(API_BASE))) {
        config.headers = { ...(config.headers || {}),
                           'X-Jarvis-Location': JSON.stringify({ status: 'off' }) };
        return config;
      }
      const { whereAmI } = await import('./whereAmI');
      const where = await whereAmI();
      config.headers = { ...(config.headers || {}),
                         'X-Jarvis-Location': JSON.stringify(where) };
    } catch {
      // Never block the request from here. The server decides whether a
      // change without a location is allowed - that rule lives in one place,
      // and it is not this one.
    }
    return config;
  });
}

export function installAuthInterceptor() {
  // Always, whether or not sign-in is configured.
  installDegradedReporting();
  installLocationInterceptor();

  if (installed || !authConfigured) return;
  installed = true;

  axios.interceptors.request.use(async (config) => {
    const headers = config.headers || {};
    const existing = headers.Authorization || headers.authorization;
    if (hasRealBearer(existing)) return config;
    if (!isOurApi(config.url)) return config;

    try {
      // Asking the server to answer as someone else. It verifies the caller is
      // an administrator before honouring it - this header is a request, not a
      // claim - so it is sent independently of whether a token was obtained.
      // Tying the two together made the flow untestable without a live Entra
      // sign-in, which is how three regressions in a row reached production.
      const extra = {};
      const as = storedViewAsUserId();
      if (as) extra['X-View-As-User'] = as;
      // Previewing the contractor portal as a contractor: nothing about the
      // request changes - the administrator reads the portal as themselves -
      // but the server records the preview in the Audit log by this name.
      const asContractor = storedViewAsContractorId();
      if (asContractor) extra['X-View-As-Contractor'] = asContractor;

      const token = await getApiToken();
      if (token) { extra.Authorization = `Bearer ${token}`; config.jarvisStaffSigned = true; }
      if (Object.keys(extra).length > 0) config.headers = { ...headers, ...extra };
    } catch {
      // Anonymous is a valid state; the server decides what it can reach.
    }
    return config;
  });

}
